<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: SurnameProfilePanel                                   |
| Section: components                                              |
| Purpose: Summary panel for a surname's geographic distribution.  |
+------------------------------------------------------------------+
-->
<template>
    <div :class="['surname-profile-panel', {
        'surname-profile-panel--visible': visible,
        'surname-profile-panel--inline': inline,
    }]" :aria-hidden="visible ? 'false' : 'true'" :inert="!visible">
        <div class="surname-profile-panel__overlay" @click="$emit('close')"></div>

        <button v-if="visible" type="button" class="surname-profile-panel__close-external"
            :aria-label="$t('placeMap.surnameProfile.close')" @click="$emit('close')">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
            </svg>
        </button>

        <section class="surname-profile-panel__content" role="dialog" aria-modal="true"
            :aria-labelledby="visible ? 'surname-profile-title' : undefined">
            <header class="surname-profile-panel__header">
                <p class="surname-profile-panel__eyebrow">{{ $t('placeMap.surnameProfile.eyebrow') }}</p>
                <h2 id="surname-profile-title" class="surname-profile-panel__title">
                    {{ profile?.summary?.surname || selectedLabel?.surname || '' }}
                </h2>
                <p class="surname-profile-panel__subtitle">{{ $t('placeMap.surnameProfile.subtitle') }}</p>
            </header>

            <div v-if="loading" class="surname-profile-panel__state" role="status">
                <span class="surname-profile-panel__spinner" aria-hidden="true"></span>
                <span>{{ $t('placeMap.surnameProfile.loading') }}</span>
            </div>
            <div v-else-if="error" class="surname-profile-panel__state surname-profile-panel__state--error">
                {{ $t('placeMap.surnameProfile.error') }}
            </div>
            <div v-else-if="profile?.summary" class="surname-profile-panel__body">
                <div class="surname-profile-panel__stats">
                    <div class="surname-profile-panel__stat">
                        <span>{{ formatNumber(profile.summary.personCount) }}</span>
                        <small>{{ $t('placeMap.surnameProfile.people') }}</small>
                    </div>
                    <div class="surname-profile-panel__stat">
                        <span>{{ formatNumber(profile.summary.regionCount) }}</span>
                        <small>{{ $t('placeMap.surnameProfile.regions') }}</small>
                    </div>
                    <div class="surname-profile-panel__stat">
                        <span>{{ periodLabel }}</span>
                        <small>{{ $t('placeMap.surnameProfile.period') }}</small>
                    </div>
                </div>

                <section v-if="selectedLabel" class="surname-profile-panel__section">
                    <h3>{{ $t('placeMap.surnameProfile.selectedRegion') }}</h3>
                    <div class="surname-profile-panel__selected-region">
                        <span>{{ selectedLabel.representativePlace }}</span>
                        <strong>{{ formatPersonCount(selectedLabel.localCount) }}</strong>
                    </div>
                </section>

                <section v-if="profile.summary.topAreas?.length" class="surname-profile-panel__section">
                    <h3>{{ $t('placeMap.surnameProfile.topAreas') }}</h3>
                    <div class="surname-profile-panel__areas">
                        <button v-for="area in profile.summary.topAreas" :key="`${area.name}-${area.latitude}-${area.longitude}`"
                            type="button" @click="$emit('select-area', area)">
                            <span>{{ area.name }}</span>
                            <strong>{{ formatNumber(area.count) }}</strong>
                        </button>
                    </div>
                </section>
            </div>
            <div v-else class="surname-profile-panel__state">
                {{ $t('placeMap.surnameProfile.empty') }}
            </div>
        </section>
    </div>
</template>

<script>
export default {
    name: 'SurnameProfilePanel',
    emits: ['close', 'select-area'],
    props: {
        error: {
            type: [Boolean, String],
            default: false,
        },
        inline: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        profile: {
            type: Object,
            default: null,
        },
        selectedLabel: {
            type: Object,
            default: null,
        },
        visible: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        periodLabel() {
            const fromYear = this.profile?.summary?.fromYear;
            const toYear = this.profile?.summary?.toYear;
            if (!fromYear && !toYear) return this.$t('placeMap.surnameProfile.unknownPeriod');
            if (fromYear && toYear && fromYear !== toYear) return `${fromYear}–${toYear}`;
            return String(fromYear || toYear);
        },
    },
    methods: {
        formatNumber(value) {
            return Number(value || 0).toLocaleString(this.$i18n?.locale || 'nl-NL');
        },
        formatPersonCount(value) {
            const count = Number(value || 0);
            return this.$t(
                count === 1
                    ? 'placeMap.surnameProfile.personCountOne'
                    : 'placeMap.surnameProfile.personCountMany',
                { count: this.formatNumber(count) }
            );
        },
    },
};
</script>

<style scoped>
.surname-profile-panel {
    position: fixed;
    inset: 0;
    z-index: 1000;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.25s ease;
}

.surname-profile-panel--inline {
    position: absolute;
}

.surname-profile-panel--visible {
    pointer-events: auto;
    opacity: 1;
}

.surname-profile-panel__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.42);
    opacity: 0;
    transition: opacity 0.25s ease;
}

.surname-profile-panel--visible .surname-profile-panel__overlay {
    opacity: 1;
}

.surname-profile-panel__content {
    position: absolute;
    inset: 0 0 0 auto;
    width: 440px;
    max-width: 90vw;
    overflow-y: auto;
    background: #fff;
    color: var(--brand-text-dark);
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.2);
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.surname-profile-panel--visible .surname-profile-panel__content {
    transform: translateX(0);
}

.surname-profile-panel__close-external {
    position: absolute;
    top: 32px;
    right: 452px;
    z-index: 1001;
    display: grid;
    width: 40px;
    height: 40px;
    padding: 10px;
    place-items: center;
    color: var(--brand-text-dark);
    cursor: pointer;
    background: #fff;
    border: 0;
    border-radius: var(--border-radius-md);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.surname-profile-panel__header {
    padding: 32px;
    border-bottom: 1px solid #e5e5e5;
}

.surname-profile-panel__eyebrow {
    margin: 0 0 6px;
    color: #c62828;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.surname-profile-panel__title {
    margin: 0;
    font-size: 30px;
    line-height: 1.2;
}

.surname-profile-panel__subtitle {
    margin: 8px 0 0;
    color: #666;
    font-size: 14px;
    line-height: 1.5;
}

.surname-profile-panel__body {
    padding: 24px 32px 32px;
}

.surname-profile-panel__stats {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
}

.surname-profile-panel__stat {
    min-width: 0;
    padding: 14px 10px;
    text-align: center;
    background: #f6f3ef;
    border-radius: var(--border-radius-md);
}

.surname-profile-panel__stat span {
    display: block;
    overflow: hidden;
    font-size: 18px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.surname-profile-panel__stat small {
    display: block;
    margin-top: 4px;
    color: #666;
    font-size: 11px;
}

.surname-profile-panel__section {
    margin-top: 28px;
}

.surname-profile-panel__section h3 {
    margin: 0 0 12px;
    font-size: 16px;
}

.surname-profile-panel__selected-region,
.surname-profile-panel__areas button {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 14px;
    align-items: center;
    width: 100%;
    padding: 13px 14px;
    text-align: left;
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: var(--border-radius-md);
}

.surname-profile-panel__selected-region span,
.surname-profile-panel__areas span {
    overflow: hidden;
    font-weight: 600;
    line-height: 1.35;
    text-overflow: ellipsis;
}

.surname-profile-panel__selected-region strong,
.surname-profile-panel__areas strong {
    color: #666;
    font-size: 13px;
    white-space: nowrap;
}

.surname-profile-panel__areas {
    display: grid;
    gap: 10px;
}

.surname-profile-panel__areas button {
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease;
}

.surname-profile-panel__areas button:hover,
.surname-profile-panel__areas button:focus-visible {
    background: #fff7f7;
    border-color: #c62828;
}

.surname-profile-panel__state {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    padding: 32px;
    color: #666;
    text-align: center;
}

.surname-profile-panel__state--error {
    color: #c62828;
}

.surname-profile-panel__spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(198, 40, 40, 0.2);
    border-top-color: #c62828;
    border-radius: 50%;
    animation: surname-profile-spin 0.8s linear infinite;
}

@keyframes surname-profile-spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 768px) {
    .surname-profile-panel__content {
        width: 100%;
        max-width: 100vw;
    }

    .surname-profile-panel__close-external {
        top: 16px;
        right: 8px;
    }

    .surname-profile-panel__header,
    .surname-profile-panel__body {
        padding-right: 20px;
        padding-left: 20px;
    }

}
</style>
